import { formatMoney, formatMoneyShort, type ProjectFunnelDto } from '@nsb/core';

/**
 * The funnel's key (L12; Jason, 2026-10-06: a gate is done or not done yet, never passed): only what the funnel in
 * front of the reader draws, each in the words of what is built. The marks by what they are (a step that closes a
 * phase, a milestone, a step that delivers an estimate) and by where they stand (done, next, not done yet), the "~"
 * before a date that is a plan (never before a gate placed by its phase's share, which says it is not dated), the band
 * so far and ahead, what is billed, the budget and what is left for the work
 * under it, what runs over it, and the schedule's give or take, plan and push. The words of the made-up end the lines
 * ahead meet on (L-11) are here too, for the drawing, its key and the sentence over it alike.
 */
export type FunnelKeyEntry = 'closes' | 'milestone' | 'estimate' | 'set' | 'done' | 'next' | 'notYet' | 'about' | 'soFar' | 'spread' | 'ahead' | 'billed' | 'budget' | 'work' | 'over' | 'span' | 'planned' | 'pushed';

export const FUNNEL_KEY_WORDS: Record<FunnelKeyEntry, string> = {
  closes: 'A step that closes a phase',
  milestone: 'A milestone: a point the work reaches',
  estimate: 'A step that delivers an estimate',
  set: 'An estimate set, on the step it came from: press it for what it is',
  done: 'Done',
  next: 'Next',
  notYet: 'Not done yet',
  about: 'About then, as planned',
  soFar: 'The estimate, as set',
  spread: 'How sure the price is, either way, as each phase closes',
  ahead: 'Ahead: closing on the middle of the last estimate at the far end',
  billed: 'Billed',
  budget: 'Budget, all in',
  work: 'For the work, under the budget',
  over: 'Over the budget',
  span: 'Give or take',
  planned: 'Planned',
  pushed: 'Pushed out',
};

/**
 * What the lines ahead meet on at the frame's far end (L-11; Jason, 2026-10-07: "this should show the estimated final
 * price based on the fact it is the last average of the band, it really needs to get across that this is purely made
 * up"): the confirmed budget's amount for the work, else the middle of the last estimate. The one figure printed ahead
 * of today, never one anyone set.
 */
export const END_WORDS = { budget: 'the budget for the work', middle: 'the middle of the last estimate' } as const;

/** The point the lines ahead meet on, and what it is made up from; none once the project has ended, or with nothing ahead. */
export function aheadEnd(f: Pick<ProjectFunnelDto, 'band' | 'cap' | 'endedOn' | 'prototype'>): { cents: number; from: keyof typeof END_WORDS } | null {
  const last = f.band.at(-1);
  if (f.prototype || f.endedOn || !last?.projected) return null;
  return { cents: last.highCents, from: f.cap ? 'budget' : 'middle' };
}

/** What the end is made up from, in the reader's words: the client's own budget is theirs. */
export const endFrom = (from: keyof typeof END_WORDS, client = false): string => (client && from === 'budget' ? 'your budget for the work' : END_WORDS[from]);

/** The end as the drawing labels it: its figure, "≈ $387k", over what it is made up from. */
export function endLabel(f: Pick<ProjectFunnelDto, 'band' | 'cap' | 'endedOn' | 'prototype'>): { figure: string; madeUp: string } | null {
  const end = aheadEnd(f);
  return end ? { figure: `≈ ${formatMoneyShort(end.cents)}`, madeUp: `made up: ${END_WORDS[end.from]}` } : null;
}

/** The end in a sentence: "about $387,000, made up: your budget for the work". */
export function endInWords(f: Pick<ProjectFunnelDto, 'band' | 'cap' | 'endedOn' | 'prototype'>, client = false): string | null {
  const end = aheadEnd(f);
  return end ? `about ${formatMoney(end.cents)}, made up: ${endFrom(end.from, client)}` : null;
}

/** An entry in words for this funnel: what lies ahead closes on the budget for the work when one is confirmed (Jason, 2026-10-07, L-8). */
export function keyWords(e: FunnelKeyEntry, f: Pick<ProjectFunnelDto, 'cap'>): string {
  return e === 'ahead' && f.cap ? `Ahead: closing on ${END_WORDS.budget} at the far end` : FUNNEL_KEY_WORDS[e];
}

export function funnelKey(f: ProjectFunnelDto): FunnelKeyEntry[] {
  // The master's prototypical job (L-12): the steps that close its phases and how sure the price is as each does.
  if (f.prototype) {
    const has: Partial<Record<FunnelKeyEntry, boolean>> = { closes: f.gates.some((g) => g.on && g.mark === 'gate'), milestone: f.gates.some((g) => g.on && g.mark === 'ring'), spread: f.band.length > 1 };
    return (Object.keys(FUNNEL_KEY_WORDS) as FunnelKeyEntry[]).filter((k) => has[k]);
  }
  const dated = f.gates.filter((g) => g.on);
  const ahead = dated.filter((g) => g.state !== 'passed');
  // Planned by someone: a gate placed by its phase's share has no "~" and no give or take.
  const planned = ahead.filter((g) => !g.placed);
  const past = f.band.filter((p) => !p.projected).length;
  const projected = f.band.some((p) => p.projected);
  const s = f.schedule;
  const has: Record<FunnelKeyEntry, boolean> = {
    closes: dated.some((g) => g.mark === 'gate'),
    milestone: dated.some((g) => g.mark === 'ring' && !g.deliversEstimate),
    estimate: dated.some((g) => g.deliversEstimate),
    set: f.estimates.some((e) => e.on >= f.startOn && e.on <= (f.endedOn ?? f.today)),
    done: dated.some((g) => g.state === 'passed'),
    next: dated.some((g) => g.state === 'next'),
    notYet: dated.some((g) => g.state === 'upcoming'),
    about: planned.length > 0,
    soFar: past > 1,
    spread: false,
    ahead: past > 0 && projected,
    billed: f.spent.length > 0,
    budget: f.ceiling !== null,
    work: Boolean(f.cap && f.cap.workCents < f.cap.totalCents),
    over: f.band.some((p) => (p.overCents ?? 0) > 0),
    span: planned.some((g) => g.giveOrTakeDays > 0),
    planned: Boolean(s.projectedEndOn || s.plannedEndOn),
    pushed: (s.slipDays > 0 && Boolean(s.plannedEndOn && s.projectedEndOn)) || dated.some((g) => g.originalOn && g.slipDays > 0),
  };
  return (Object.keys(FUNNEL_KEY_WORDS) as FunnelKeyEntry[]).filter((k) => has[k]);
}
